"use client";

import { useEffect } from "react";

/**
 * 손님 화면에서 난 오류를 Sentry 로 보낸다.
 *
 * 왜 이렇게 만들었나 (2026-08-14)
 * 백엔드에는 Sentry 가 붙어 있었지만 브라우저 오류는 아무 데도 안 쌓이고 있었다.
 * 그런데 이 화면(/play)은 첫 사진 시간을 힘들게 줄여 놓은 참이라, 수집기 때문에
 * 다시 느려지면 안 된다.
 *
 * ⭐ 처음에는 "화면이 다 뜬 뒤 한가할 때" 받아 왔는데, 재 보니 그것도 비쌌다 —
 *    @sentry/browser 묶음이 **428KB**(오류 재생용 rrweb 까지 딸려 온다)이고,
 *    첫 화면이 그려지는 1.7초쯤에 도착해 자리를 다퉜다.
 *    손님 대부분은 오류를 겪지 않는다. 그래서 규칙을 바꿨다:
 *
 *  ① **오류가 나기 전에는 아예 안 받아 온다.** 첫 오류가 난 순간에만 받는다.
 *     오류가 없는 방문(대부분)은 428KB 를 한 톨도 안 쓴다.
 *  ② **그 첫 오류도 안 놓친다.** 처음부터 귀를 열어 두고 모아 뒀다가,
 *     수집기가 붙으면 한꺼번에 보낸다.
 *
 * ⚠️ 맞바꾼 것: 오류 직전의 발자취(breadcrumb)는 얇아진다.
 *    대신 어느 파일 몇 번째 줄인지는 소스맵으로 그대로 나온다 — 그게 핵심이다.
 *
 * @sentry/nextjs 대신 @sentry/browser 를 쓴 이유: 전자는 빌드 설정(withSentryConfig)을
 * 갈아 끼우고 모든 쪽 묶음에 자동으로 끼어든다. 지금은 첫 화면 무게가 더 중요하고,
 * 서버 쪽 오류는 백엔드 Sentry 가 이미 받고 있다.
 */

const DSN = process.env.NEXT_PUBLIC_SENTRY_DSN || "";

// 굳이 우리한테 안 알려도 되는 것들 (남의 확장 프로그램·취소된 요청 등)
const IGNORE = [
  "ResizeObserver loop",
  "Non-Error promise rejection captured",
  "AbortError",
  "The play() request was interrupted",
  "chrome-extension://",
  "moz-extension://",
];

// 수집기가 붙기 전에 난 것을 담아 두는 곳 (많아야 20개)
const early = [];
let loading = false;
let sentry = null;

const isIgnorable = (text) =>
  !!text && IGNORE.some((needle) => String(text).includes(needle));

const send = (item) => {
  if (!sentry) return;
  if (item.error) sentry.captureException(item.error);
  else if (item.message) sentry.captureMessage(String(item.message));
};

// 첫 오류가 났을 때 한 번만 받아 온다
const loadOnce = async () => {
  if (loading || sentry) return;
  loading = true;
  try {
    const Sentry = await import("@sentry/browser");
    Sentry.init({
      dsn: DSN,
      // 오류만 받는다. 추적·재생은 안 켠다 — 무게와 무료 한도 때문에.
      tracesSampleRate: 0,
      ignoreErrors: IGNORE,
      // 어느 배포에서 난 것인지 알 수 있게 (소스맵과 짝을 맞추는 이름표)
      release: process.env.NEXT_PUBLIC_BUILD_ID || undefined,
      environment: process.env.NODE_ENV,
    });
    sentry = Sentry;
    for (const item of early.splice(0)) send(item);
  } catch {
    // 수집기를 못 받아 와도 화면은 그대로 돌아야 한다
    loading = false;
  }
};

const ErrorReporter = () => {
  useEffect(() => {
    if (!DSN) return; // 주소가 없으면 아무 일도 안 한다 (CI 빌드 등)

    const handle = (item) => {
      if (isIgnorable(item.message) || isIgnorable(item.error?.message)) return;
      if (sentry) {
        send(item);
        return;
      }
      if (early.length < 20) early.push(item);
      // 첫 오류가 났으니 이제 받아 온다. 화면 그리는 일과 다투지 않게
      // 브라우저가 한가해질 때까지만 기다린다.
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(loadOnce, { timeout: 3000 });
      } else {
        setTimeout(loadOnce, 0);
      }
    };

    const onError = (e) => handle({ error: e?.error, message: e?.message });
    const onReject = (e) => handle({ error: e?.reason });

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onReject);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onReject);
    };
  }, []);

  return null;
};

export default ErrorReporter;
